// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AuditContextProperty,
  AuditEventContext,
  AuditEventContextAction,
} from '@/audit/api/types';
import { AuditAssetAssignmentListLabel } from '@/audit/components/audit-asset-assignment-list-label';
import { AuditAssetMetadataLabel } from '@/audit/components/audit-asset-metadata-label';
import { AuditAssetMetadataListLabel } from '@/audit/components/audit-asset-metadata-list-label';
import { AuditAttentionThresholdListLabel } from '@/audit/components/audit-attention-threshold-list-label';
import { AuditCachedBillingDeltaList } from '@/audit/components/audit-cached-billing-delta-list-label';
import { AuditCachedBomDeltaListLabel } from '@/audit/components/audit-cached-bom-delta-list-label';
import { AuditCachedBundleMembersDeltaListLabel } from '@/audit/components/audit-cached-bundle-members-delta-list-label';
import { AuditCachedChargeDeltaLabel } from '@/audit/components/audit-cached-charge-delta-label';
import { AuditCachedEntityDeltaListLabel } from '@/audit/components/audit-cached-entity-delta-list-label';
import { AuditCachedProjectBillingDeltaList } from '@/audit/components/audit-cached-project-billing-delta-list-label';
import { AuditCarrierTrackingLabel } from '@/audit/components/audit-carrier-tracking-label';
import { AuditChargeCascadeDetails } from '@/audit/components/audit-charge-cascade-details';
import { AuditContactLabel } from '@/audit/components/audit-contact-label';
import { AuditFileDownloadLink } from '@/audit/components/audit-file-download-link';
import { AuditFileLabel } from '@/audit/components/audit-file-label';
import { AuditFileListLabel } from '@/audit/components/audit-file-list-label';
import { AuditFulfillmentManifestLabel } from '@/audit/components/audit-fulfillment-manifest-label';
import { AuditFulfillmentOrderLabel } from '@/audit/components/audit-fulfillment-order-label';
import { AuditHubspotEntityReference } from '@/audit/components/audit-hubspot-entity-reference';
import { AuditHubspotPipelineDeltaLabel } from '@/audit/components/audit-hubspot-pipeline-delta-label';
import { AuditHubspotPropertyMapping } from '@/audit/components/audit-hubspot-property-mapping';
import { AuditInAppMessageTypesLabel } from '@/audit/components/audit-in-app-message-types-label';
import { AuditInvoiceLineItemDeltaDetails } from '@/audit/components/audit-invoice-line-item-delta-details';
import { AuditLedgerAccountListLabel } from '@/audit/components/audit-ledger-account-list-label';
import { AuditLocationLabel } from '@/audit/components/audit-location-label';
import { AuditProjectLocationListLabel } from '@/audit/components/audit-location-list-label';
import { AuditMergedContactEntityLabel } from '@/audit/components/audit-merged-contact-entity-label';
import { AuditMergedCustomerEntityLabel } from '@/audit/components/audit-merged-customer-entity-label';
import { AuditMergedItemEntityLabel } from '@/audit/components/audit-merged-item-entity-label';
import { AuditMergedLocationEntityLabel } from '@/audit/components/audit-merged-location-entity-label';
import { AuditNoteListLabel } from '@/audit/components/audit-note-list-label';
import { AuditNotificationConfigurationPropertyList } from '@/audit/components/audit-notification-configuration-property-list';
import { AuditOAuthScopeDeltaListLabel } from '@/audit/components/audit-oauth-scope-delta-list-label';
import { AuditProjectCostLabel } from '@/audit/components/audit-project-cost-list-label';
import { AuditProjectCostTime } from '@/audit/components/audit-project-cost-time-label';
import { AuditProjectItemListPropertyLabel } from '@/audit/components/audit-project-item-list-property-label';
import { AuditProjectLineItemsListLabel } from '@/audit/components/audit-project-line-items-list-label';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { AuditPurchaseOrderLabel } from '@/audit/components/audit-purchase-order-label';
import { AuditPurchaseOrderManifestLabel } from '@/audit/components/audit-purchase-order-manifest-label';
import { AuditReturnOrderLabel } from '@/audit/components/audit-return-order-label';
import { AuditReturnOrderManifestLabel } from '@/audit/components/audit-return-order-manifest-label';
import { AuditSerialPatternDeltaListLabel } from '@/audit/components/audit-serial-pattern-delta-list-label';
import { AuditShipmentManifestLabel } from '@/audit/components/audit-shipment-manifest-label';
import { AuditShipmentQuarantineLabel } from '@/audit/components/audit-shipment-quarantine-label';
import { AuditShipmentQuarantineResolutionLabel } from '@/audit/components/audit-shipment-quarantine-resolution-label';
import { AuditTransportReferenceLabel } from '@/audit/components/audit-transport-reference-label';
import { AuditUrlLinkLabel } from '@/audit/components/audit-url-link-label';
import { AuditUrlLinkListLabel } from '@/audit/components/audit-url-link-list-label';
import { AuditWorkflowSelectionLabel } from '@/audit/components/audit-workflow-selection-label';
import { AuditWorkflowStageCascadeList } from '@/audit/components/audit-workflow-stage-cascade-list';
import { AuditWorkflowStageListLabel } from '@/audit/components/audit-workflow-stage-list-label';
import { isMultilineAuditValue } from '@/audit/utils/is-multiline-audit-value';
import { Link } from '@/components/link';
import { MinuteDuration } from '@/components/minute-duration';
import {
  ArrowForwardRounded,
  OpenInNewRounded,
} from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import { Box, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

// The backend's CachedFieldInAppMessageTypes property
const IN_APP_MESSAGE_TYPES_PROPERTY_ID = '0d884ad0-ac4f-4bd4-a0b2-0fb3c5f2486f';

interface Props {
  /**
   * The asset property to render.
   */
  property: AuditContextProperty;

  isDelete?: boolean;

  action: AuditEventContextAction;

  context: AuditEventContext;
}

function renderSingleValueLabel(
  name: string,
  value: string | number | boolean | DateTime | null
) {
  if (isMultilineAuditValue(value)) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" fontWeight="bold">
          {name}:
        </Typography>
        <Stack paddingLeft={1}>
          <AuditPropertyValue value={value} variant="caption" />
        </Stack>
      </Stack>
    );
  }

  return (
    <Typography
      display="flex"
      variant="caption"
      alignItems="center"
      sx={{ flexWrap: 'wrap' }}
    >
      <Typography
        component="span"
        variant="inherit"
        fontWeight="bold"
        sx={{ flexShrink: 0 }}
      >
        {name}:&nbsp;
      </Typography>
      <AuditPropertyValue value={value} />
    </Typography>
  );
}

/**
 * A component that renders a single property for an audit entry.
 */
export function AuditPropertyLabel(props: Props) {
  if (props.property.redacted === true) {
    return null;
  }

  if (props.property.type === 'SYSTEM_ENUM') {
    return null;
  }

  if (props.property.type === 'CONTACT') {
    return <AuditContactLabel property={props.property} />;
  }

  if (
    props.property.type === 'TEXT' &&
    props.property.id === IN_APP_MESSAGE_TYPES_PROPERTY_ID
  ) {
    return (
      <AuditInAppMessageTypesLabel
        oldValue={props.property.oldValue ?? null}
        newValue={props.property.newValue}
      />
    );
  }

  if (props.property.type === 'CACHED_NOTIFICATION_DELTA_LIST') {
    return (
      <AuditNotificationConfigurationPropertyList property={props.property} />
    );
  }

  if (props.property.type === 'CACHED_PROJECT_ITEM_CONFIGURATION_LIST') {
    return <AuditProjectLineItemsListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_PROJECT_ITEM_LIST_DELTA') {
    return (
      <AuditProjectItemListPropertyLabel
        property={props.property}
        action={props.action}
      />
    );
  }

  if (props.property.type === 'CACHED_LOCATION_LIST_DELTA') {
    return (
      <AuditProjectLocationListLabel
        property={props.property}
        action={props.action}
      />
    );
  }

  if (props.property.type === 'CACHED_ASSET_ASSIGNMENT_LIST') {
    return <AuditAssetAssignmentListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_ASSET_METADATA_LIST') {
    return <AuditAssetMetadataListLabel property={props.property} />;
  }
  if (props.property.type === 'TIME_COST') {
    return (
      <AuditProjectCostTime property={props.property} action={props.action} />
    );
  }

  if (props.property.type === 'CACHED_ASSET_METADATA') {
    return (
      <AuditAssetMetadataLabel
        property={props.property}
        isDelete={props.isDelete}
      />
    );
  }

  if (props.property.type === 'CACHED_ASSET_LIST') {
    const label = props.property.newValue
      .map((asset) => {
        return `${asset.serial} as of ${asset.start.toLocaleString({ dateStyle: 'medium' })}`;
      })
      .join(', ');

    return (
      <Typography
        display="flex"
        variant="caption"
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography
          component="span"
          variant="inherit"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {props.property.name}:&nbsp;
        </Typography>
        <AuditPropertyValue value={label} />
      </Typography>
    );
  }

  if (props.property.type === 'CACHED_LOCATION') {
    return <AuditLocationLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_FULFILLMENT_ORDER') {
    return <AuditFulfillmentOrderLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_RETURN_ORDER') {
    return <AuditReturnOrderLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_PURCHASE_ORDER') {
    return <AuditPurchaseOrderLabel property={props.property} />;
  }

  if (props.property.type === 'CARRIER_TRACKING') {
    return <AuditCarrierTrackingLabel property={props.property} />;
  }

  if (props.property.type === 'TRANSPORT_REFERENCE') {
    return <AuditTransportReferenceLabel property={props.property} />;
  }

  if (props.property.type === 'SITE_METADATA') {
    const oldAddress =
      props.property.oldValue?.address?.formattedAddress ?? null;
    const newAddress =
      props.property.newValue?.address?.formattedAddress ?? null;

    if (props.property.oldValue === undefined) {
      return renderSingleValueLabel(props.property.name, newAddress);
    }

    return (
      <AuditPropertyUpdateLabel
        name={props.property.name}
        oldValue={oldAddress}
        newValue={newAddress}
      />
    );
  }

  if (props.property.type === 'HUBSPOT_ENTITY_REFERENCE') {
    return <AuditHubspotEntityReference property={props.property} />;
  }
  if (props.property.type === 'HUBSPOT_PROPERTY_MAPPING_LIST') {
    return <AuditHubspotPropertyMapping property={props.property} />;
  }

  if (props.property.type === 'FILE') {
    const oldFile = props.property.oldValue ?? null;
    const newFile = props.property.newValue ?? null;
    // An add carries only the new file, a remove only the old, a rename both;
    // the arrow belongs to a rename alone.
    const primaryFile = oldFile ?? newFile;
    const isRename = oldFile !== null && newFile !== null;
    return (
      <Typography
        display="flex"
        variant="caption"
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography
          component="span"
          variant="inherit"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {props.property.name}:&nbsp;
        </Typography>
        {primaryFile ? (
          <AuditFileDownloadLink
            fileKey={primaryFile.key}
            filename={primaryFile.filename}
          />
        ) : (
          <AuditPropertyValue value={null} />
        )}
        {isRename && newFile && (
          <>
            {' '}
            <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
            <AuditFileDownloadLink
              fileKey={newFile.key}
              filename={newFile.filename}
            />
          </>
        )}
      </Typography>
    );
  }

  if (props.property.type === 'FILE_LIST') {
    return (
      <AuditFileListLabel property={props.property} action={props.action} />
    );
  }

  if (props.property.type === 'PROJECT_COST_LIST') {
    return (
      <AuditProjectCostLabel property={props.property} action={props.action} />
    );
  }

  if (props.property.type === 'NOTE_LIST') {
    return (
      <AuditNoteListLabel property={props.property} action={props.action} />
    );
  }

  if (props.property.type === 'URL_LINK') {
    return (
      <AuditUrlLinkLabel property={props.property} action={props.action} />
    );
  }

  if (props.property.type === 'CACHED_SHIPMENT_MANIFEST') {
    return (
      <AuditShipmentManifestLabel
        property={props.property}
        operation={
          props.context.type === 'SHIPMENT'
            ? props.context.operation
            : undefined
        }
      />
    );
  }

  if (props.property.type === 'CACHED_SHIPMENT_QUARANTINE') {
    return <AuditShipmentQuarantineLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_SHIPMENT_QUARANTINE_RESOLUTION') {
    return <AuditShipmentQuarantineResolutionLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_PURCHASE_ORDER_MANIFEST') {
    return <AuditPurchaseOrderManifestLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_RETURN_ORDER_MANIFEST') {
    return <AuditReturnOrderManifestLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_FULFILLMENT_MANIFEST') {
    return <AuditFulfillmentManifestLabel property={props.property} />;
  }

  if (props.property.type === 'URL_LINK_LIST') {
    return (
      <AuditUrlLinkListLabel property={props.property} action={props.action} />
    );
  }

  if (
    props.property.type === 'CACHED_PROJECT_FILE' ||
    props.property.type === 'CACHED_TICKET_FILE'
  ) {
    return <AuditFileLabel property={props.property} action={props.action} />;
  }

  if (
    props.property.type === 'CACHED_ASSET_DELTA_LIST' ||
    props.property.type === 'CACHED_PROJECT_DELTA_LIST'
  ) {
    return <AuditCachedEntityDeltaListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_CUSTOMER_MERGE') {
    return <AuditMergedCustomerEntityLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_CONTACT_MERGE') {
    return <AuditMergedContactEntityLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_LOCATION_MERGE') {
    return <AuditMergedLocationEntityLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_ITEM_MERGE') {
    return <AuditMergedItemEntityLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_ITEM_BILLING_DELTA_LIST') {
    return <AuditCachedProjectBillingDeltaList property={props.property} />;
  }

  if (props.property.type === 'CACHED_BILLING_DELTA_LIST') {
    if (props.context.type !== 'PROJECT_BILLING') {
      return null;
    }
    return (
      <AuditCachedBillingDeltaList
        property={props.property}
        itemContext={props.context.itemContext}
        lineItemContext={{
          quantity: props.context.lineItemQuantity,
          unitPrice: props.context.lineItemUnitPrice,
        }}
      />
    );
  }

  if (props.property.type === 'CACHED_ITEM_BOM_DELTA_LIST') {
    return <AuditCachedBomDeltaListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_BUNDLE_MEMBERS_DELTA_LIST') {
    return <AuditCachedBundleMembersDeltaListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_CHARGE_DELTA') {
    return <AuditCachedChargeDeltaLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_CHARGE_CASCADE_LIST') {
    return <AuditChargeCascadeDetails property={props.property} />;
  }

  if (props.property.type === 'CACHED_INVOICE_LINE_ITEM_DELTA_LIST') {
    return <AuditInvoiceLineItemDeltaDetails property={props.property} />;
  }

  if (props.property.type === 'CACHED_INVOICE_CONTEXT') {
    const invoice = props.property.newValue;
    if (!invoice) return null;
    return (
      <Typography variant="caption" display="block">
        <b>{props.property.name}:</b> {invoice.invoiceNumber}
      </Typography>
    );
  }

  if (props.property.type === 'CACHED_SHIPMENT') {
    const shipment = props.property.newValue;
    if (!shipment) return null;
    return (
      <Typography variant="caption" display="block">
        <b>{props.property.name}:</b>{' '}
        <Link
          to="/shipments/$shipmentKey"
          params={{ shipmentKey: shipment.key }}
          target="_blank"
          rel="noreferrer"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {shipment.shipmentNumber}
          <OpenInNewRounded fontSize="inherit" />
        </Link>
      </Typography>
    );
  }

  if (props.property.type === 'CACHED_INVOICE_REFERENCE') {
    const ref = props.property.newValue;
    if (!ref) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" display="block">
          <b>{props.property.name}:</b> {ref.invoice.invoiceNumber} (
          {pluralize(ref.charges.length, { singular: 'charge' })})
        </Typography>
      </Box>
    );
  }

  if (props.property.type === 'CACHED_HUBSPOT_PIPELINE_DELTA') {
    return <AuditHubspotPipelineDeltaLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_LEDGER_ACCOUNT_LIST') {
    return <AuditLedgerAccountListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_ATTENTION_THRESHOLD_LIST') {
    return <AuditAttentionThresholdListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_SERIAL_PATTERN_DELTA_LIST') {
    return <AuditSerialPatternDeltaListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_OAUTH_SCOPE_DELTA_LIST') {
    return <AuditOAuthScopeDeltaListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_WORKFLOW_SELECTION') {
    return <AuditWorkflowSelectionLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_WORKFLOW_STAGE_LIST') {
    return <AuditWorkflowStageListLabel property={props.property} />;
  }

  if (props.property.type === 'CACHED_WORKFLOW_STAGE_CASCADE_LIST') {
    const projectName =
      props.context.type === 'PROJECT_WORKFLOW_CASCADE'
        ? props.context.project.name
        : undefined;
    return (
      <AuditWorkflowStageCascadeList
        property={props.property}
        projectName={projectName}
      />
    );
  }

  if (props.property.type === 'INTEGRATION_STATUS') {
    const formatIntegrationStatus = (
      value: { name: string; status: string } | null
    ) => (value ? `${value.name}: ${value.status}` : null);

    return (
      <Typography
        display="flex"
        variant="caption"
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography
          component="span"
          variant="inherit"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {props.property.name}:&nbsp;
        </Typography>
        {props.property.oldValue !== undefined && (
          <AuditPropertyValue
            value={formatIntegrationStatus(props.property.oldValue)}
          />
        )}
        {props.property.oldValue !== undefined &&
          props.property.newValue !== undefined && (
            <>
              {' '}
              <ArrowForwardRounded
                fontSize="inherit"
                sx={{ marginX: 0.5 }}
              />{' '}
            </>
          )}
        {props.property.newValue !== undefined && (
          <AuditPropertyValue
            value={formatIntegrationStatus(props.property.newValue)}
          />
        )}
      </Typography>
    );
  }
  if (props.property.type === 'PAYMENT_TERM') {
    return (
      <Typography
        display="flex"
        variant="caption"
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography
          component="span"
          variant="inherit"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {props.property.name}:&nbsp;
        </Typography>
        {props.property.oldValue !== undefined && (
          <AuditPropertyValue value={props.property.oldValue?.label ?? null} />
        )}

        {props.action === 'UPDATE' && (
          <>
            {' '}
            <ArrowForwardRounded
              fontSize="inherit"
              sx={{ marginX: 0.5 }}
            />{' '}
          </>
        )}
        {props.property.newValue !== undefined && (
          <AuditPropertyValue value={props.property.newValue?.label ?? null} />
        )}
      </Typography>
    );
  }

  if (props.property.type === 'DURATION_MINUTES') {
    return (
      <Typography
        display="flex"
        variant="caption"
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography
          component="span"
          variant="inherit"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {props.property.name}:&nbsp;
        </Typography>
        {props.property.oldValue !== undefined && (
          <>
            {props.property.oldValue !== null ? (
              <MinuteDuration
                minutes={props.property.oldValue}
                variant="inherit"
              />
            ) : (
              <AuditPropertyValue value={null} />
            )}
          </>
        )}

        {props.property.oldValue !== undefined &&
          props.property.newValue !== undefined && (
            <>
              {' '}
              <ArrowForwardRounded
                fontSize="inherit"
                sx={{ marginX: 0.5 }}
              />{' '}
            </>
          )}
        {props.property.newValue !== undefined && (
          <>
            {props.property.newValue !== null ? (
              <MinuteDuration
                minutes={props.property.newValue}
                variant="inherit"
              />
            ) : (
              <AuditPropertyValue value={null} />
            )}
          </>
        )}
      </Typography>
    );
  }

  if (props.property.type === 'REMOTE_INTEGRATION_ITEM') {
    const value = props.property.newValue;
    if (!value) {
      return null;
    }

    const integrationDisplayNames: Record<string, string> = {
      NETSUITE: 'NetSuite',
      QBO: 'QuickBooks',
    };
    const integrationName =
      integrationDisplayNames[value.integrationType] ?? value.integrationType;

    return (
      <Typography variant="caption" component="div">
        <Typography component="span" variant="inherit" fontWeight="bold">
          {integrationName} item:
        </Typography>
        <Typography variant="inherit" sx={{ paddingLeft: 2 }}>
          SKU: {value.sku || '—'}
        </Typography>
        <Typography variant="inherit" sx={{ paddingLeft: 2 }}>
          Name: {value.name || '—'}
        </Typography>
      </Typography>
    );
  }

  if (props.property.oldValue === undefined) {
    const newValue = props.property.newValue;

    if (props.property.type === 'DATE_TIME' && newValue instanceof DateTime) {
      return renderSingleValueLabel(
        props.property.name,
        newValue.toLocaleString({ dateStyle: 'medium', timeStyle: 'short' })
      );
    }

    return renderSingleValueLabel(props.property.name, newValue);
  }

  if (
    isMultilineAuditValue(props.property.oldValue) ||
    isMultilineAuditValue(props.property.newValue)
  ) {
    return (
      <AuditPropertyUpdateLabel
        name={props.property.name}
        oldValue={props.property.oldValue}
        newValue={props.property.newValue}
        variant="caption"
      />
    );
  }

  const isDateTime = props.property.type === 'DATE_TIME';
  const formatLabel = isDateTime
    ? (value: string | number | boolean | DateTime) => {
        if (value instanceof DateTime) {
          return value.toLocaleString({
            dateStyle: 'medium',
            timeStyle: 'short',
          });
        }
        return String(value);
      }
    : undefined;

  return (
    <Typography
      display="flex"
      variant="caption"
      alignItems="center"
      sx={{ flexWrap: 'wrap' }}
    >
      <Typography
        component="span"
        variant="inherit"
        fontWeight="bold"
        sx={{ flexShrink: 0 }}
      >
        {props.property.name}:&nbsp;
      </Typography>
      <AuditPropertyValue
        value={props.property.oldValue}
        getLabelForValue={formatLabel}
      />
      {props.action !== 'DELETE' && props.property.newValue !== undefined && (
        <>
          {' '}
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
          <AuditPropertyValue
            value={props.property.newValue}
            getLabelForValue={formatLabel}
          />
        </>
      )}
    </Typography>
  );
}
